import { useEffect, useState } from "react";
import api, { formatApiErrorDetail, BACKEND_URL } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { toast } from "sonner";

const resolveImg = (url) =>
  url && url.startsWith("/api") ? `${BACKEND_URL}${url}` : url;

const auctionStatusClass = (status) => {
  if (status === "live") return "bg-[#1E3A8A] text-[#F4C430]";
  if (status === "sold") return "bg-[#D32F2F] text-white";
  return "bg-[#E5E0D8] text-[#555]";
};

const artworkAvailabilityLabel = (artwork, inAuction) => {
  if (artwork.sold) return "Sold";
  if (inAuction) return "In auction";
  return "Available";
};

const tabCount = (key, counts) => counts[key] ?? 0;

const MEDIUMS = ["Painting", "Mixed Media", "Sculpture", "Photography", "Digital"];
const TABS = [
  { key: "artworks", label: "Artworks" },
  { key: "auctions", label: "Auctions" },
  { key: "orders", label: "Orders" },
];

const inputCls =
  "mt-2 rounded-none border-b-2 border-l-0 border-r-0 border-t-0 border-[#E5E0D8] bg-transparent px-0 py-2 focus-visible:ring-0 focus-visible:border-[#1E3A8A]";

export default function AdminCatalogue() {
  const [tab, setTab] = useState("artworks");
  const [artworks, setArtworks] = useState([]);
  const [auctions, setAuctions] = useState([]);
  const [orders, setOrders] = useState([]);
  const [artists, setArtists] = useState([]);

  const [editing, setEditing] = useState(null); // artwork being edited
  const [creating, setCreating] = useState(false); // create artwork dialog
  const [auctionFor, setAuctionFor] = useState(null); // artwork to auction

  const load = async () => {
    try {
      const [a, b, c, d] = await Promise.all([
        api.get("/admin/artworks", { params: { limit: 200 } }),
        api.get("/admin/auctions"),
        api.get("/admin/orders"),
        api.get("/artists"),
      ]);
      setArtworks(a.data.items || []);
      setAuctions(b.data);
      setOrders(c.data);
      setArtists(d.data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not load catalogue");
    }
  };

  useEffect(() => {
    load();
  }, []);

  const auctionedIds = new Set(auctions.filter((x) => x.status === "live" || x.status === "scheduled").map((x) => x.artwork_id));

  const deleteArtwork = async (id) => {
    if (!window.confirm("Delete this artwork? This cannot be undone.")) return;
    try {
      await api.delete(`/admin/artworks/${id}`);
      setArtworks((a) => a.filter((x) => x.id !== id));
      toast.success("Artwork deleted");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Delete failed");
    }
  };

  const togglePublish = async (art) => {
    const path = art.status === "draft" ? "publish" : "unpublish";
    try {
      await api.post(`/admin/artworks/${art.id}/${path}`);
      setArtworks((prev) =>
        prev.map((a) =>
          a.id === art.id ? { ...a, status: a.status === "draft" ? "published" : "draft" } : a
        )
      );
      toast.success(art.status === "draft" ? "Published" : "Reverted to draft");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not update");
    }
  };

  const closeAuction = async (id) => {
    if (!window.confirm("Force-close this auction now?")) return;
    try {
      const { data } = await api.post(`/admin/auctions/${id}/close`);
      setAuctions((a) => a.map((x) => (x.id === id ? { ...x, ...data } : x)));
      toast.success("Auction closed");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Close failed");
    }
  };

  return (
    <div data-testid="admin-catalogue">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <p className="label-eyebrow text-[#1E3A8A]">— Inventory</p>
          <h1 className="font-serif-display text-5xl tracking-tighter font-medium mt-3 text-[#111]">
            Catalogue
          </h1>
          <p className="text-[#555] mt-3">Create, edit, auction, and moderate every work on the platform.</p>
        </div>
        {tab === "artworks" && (
          <Button
            data-testid="admin-create-artwork-btn"
            onClick={() => setCreating(true)}
            className="rounded-none bg-[#1E3A8A] hover:bg-[#162a66] text-white px-6 py-5 h-auto label-eyebrow"
          >
            + Add Artwork
          </Button>
        )}
      </div>

      <div className="mt-8 flex gap-2 border-b border-[#E5E0D8]">
        {TABS.map((t) => (
          <button
            key={t.key}
            data-testid={`catalogue-tab-${t.key}`}
            onClick={() => setTab(t.key)}
            className={`label-eyebrow px-5 py-3 -mb-px border-b-2 transition-colors ${
              tab === t.key
                ? "border-[#1E3A8A] text-[#1E3A8A]"
                : "border-transparent text-[#555] hover:text-[#111]"
            }`}
          >
            {t.label} ({tabCount(t.key, { artworks: artworks.length, auctions: auctions.length, orders: orders.length })})
          </button>
        ))}
      </div>

      <div className="mt-8">
        {tab === "artworks" && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {artworks.map((a) => (
              <div key={a.id} data-testid={`admin-artwork-${a.id}`} className="border border-[#E5E0D8] bg-white">
                <div className="aspect-square bg-[#F2EDE5]">
                  <img src={resolveImg(a.image_url)} alt={a.title} className="w-full h-full object-cover" />
                </div>
                <div className="p-4">
                  <p className="label-eyebrow text-[#1E3A8A]">{a.medium} · {a.country}</p>
                  <h3 className="font-serif-display text-lg mt-1 text-[#111]">{a.title}</h3>
                  <p className="text-xs text-[#555] mt-1">{a.artist_name} · ${a.price.toLocaleString()}</p>
                  <div className="mt-2 flex items-center gap-2 flex-wrap">
                    <span className={`label-eyebrow px-2 py-0.5 ${a.status === "draft" ? "bg-[#F4C430]/30 text-[#1E3A8A]" : "bg-[#1E3A8A] text-[#F4C430]"}`}>
                      {a.status === "draft" ? "DRAFT" : "PUBLISHED"}
                    </span>
                  </div>
                  <div className="mt-3 flex items-center justify-between flex-wrap gap-2">
                    <span className={`label-eyebrow ${a.sold ? "text-[#D32F2F]" : "text-[#1E3A8A]"}`}>
                      {artworkAvailabilityLabel(a, auctionedIds.has(a.id))}
                    </span>
                    <div className="flex items-center gap-3">
                      <button
                        onClick={() => togglePublish(a)}
                        data-testid={`publish-toggle-artwork-${a.id}`}
                        className={`label-eyebrow hover:underline ${a.status === "draft" ? "text-[#1E3A8A]" : "text-[#555]"}`}
                      >
                        {a.status === "draft" ? "Publish" : "Unpublish"}
                      </button>
                      <button
                        onClick={() => setEditing(a)}
                        data-testid={`edit-artwork-${a.id}`}
                        className="label-eyebrow text-[#1E3A8A] hover:underline"
                      >
                        Edit
                      </button>
                      {!a.sold && !auctionedIds.has(a.id) && a.status === "published" && (
                        <button
                          onClick={() => setAuctionFor(a)}
                          data-testid={`auction-artwork-${a.id}`}
                          className="label-eyebrow text-[#1E3A8A] hover:underline"
                        >
                          Auction
                        </button>
                      )}
                      <button
                        onClick={() => deleteArtwork(a.id)}
                        data-testid={`delete-artwork-${a.id}`}
                        className="label-eyebrow text-[#D32F2F] hover:underline"
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {tab === "auctions" && (
          <div className="border border-[#E5E0D8] bg-white overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[#F4EFE6]/40 border-b border-[#E5E0D8]">
                <tr>
                  <th className="text-left p-3 label-eyebrow text-[#555]">Artwork</th>
                  <th className="text-left p-3 label-eyebrow text-[#555]">Artist</th>
                  <th className="text-left p-3 label-eyebrow text-[#555]">Status</th>
                  <th className="text-left p-3 label-eyebrow text-[#555]">Current bid</th>
                  <th className="text-left p-3 label-eyebrow text-[#555]">Bids</th>
                  <th className="text-left p-3 label-eyebrow text-[#555]">Ends</th>
                  <th className="text-right p-3 label-eyebrow text-[#555]">Action</th>
                </tr>
              </thead>
              <tbody>
                {auctions.map((a) => (
                  <tr key={a.id} data-testid={`admin-auction-${a.id}`} className="border-b border-[#E5E0D8] last:border-b-0">
                    <td className="p-3 font-serif-display text-base text-[#111]">{a.artwork_title}</td>
                    <td className="p-3 text-[#555]">{a.artist_name}</td>
                    <td className="p-3">
                      <span className={`label-eyebrow px-2 py-1 ${auctionStatusClass(a.status)}`}>{a.status}</span>
                    </td>
                    <td className="p-3 font-mono-aam">${(a.current_bid ?? a.starting_price).toLocaleString()}</td>
                    <td className="p-3 font-mono-aam">{a.bid_count}</td>
                    <td className="p-3 font-mono-aam text-xs text-[#555]">{a.end_time?.slice(0, 16).replace("T", " ")}</td>
                    <td className="p-3 text-right">
                      {a.status === "live" ? (
                        <button
                          onClick={() => closeAuction(a.id)}
                          data-testid={`close-auction-${a.id}`}
                          className="label-eyebrow text-[#D32F2F] hover:underline"
                        >
                          Close now
                        </button>
                      ) : (
                        <span className="label-eyebrow text-[#999]">—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {tab === "orders" && (
          <div className="border border-[#E5E0D8] bg-white overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[#F4EFE6]/40 border-b border-[#E5E0D8]">
                <tr>
                  <th className="text-left p-3 label-eyebrow text-[#555]">Order #</th>
                  <th className="text-left p-3 label-eyebrow text-[#555]">Source</th>
                  <th className="text-left p-3 label-eyebrow text-[#555]">Buyer</th>
                  <th className="text-left p-3 label-eyebrow text-[#555]">Amount</th>
                  <th className="text-left p-3 label-eyebrow text-[#555]">Status</th>
                  <th className="text-left p-3 label-eyebrow text-[#555]">Date</th>
                </tr>
              </thead>
              <tbody>
                {orders.map((o) => (
                  <tr key={o.id} data-testid={`admin-order-${o.id}`} className="border-b border-[#E5E0D8] last:border-b-0">
                    <td className="p-3 font-mono-aam text-xs">{o.id.slice(0, 8)}</td>
                    <td className="p-3 label-eyebrow text-[#1E3A8A]">{o.source || "checkout"}</td>
                    <td className="p-3 text-[#333]">{o.buyer_email}</td>
                    <td className="p-3 font-mono-aam">${o.amount.toLocaleString()}</td>
                    <td className="p-3">
                      <span className={`label-eyebrow px-2 py-1 ${
                        o.payment_status === "paid"
                          ? "bg-[#1E3A8A] text-[#F4C430]"
                          : "bg-[#E5E0D8] text-[#555]"
                      }`}>{o.payment_status}</span>
                    </td>
                    <td className="p-3 font-mono-aam text-xs text-[#555]">{o.created_at?.slice(0, 16).replace("T", " ")}</td>
                  </tr>
                ))}
                {orders.length === 0 && (
                  <tr><td colSpan={6} className="p-8 text-center text-[#555]">No orders yet.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <EditArtworkDialog
        artwork={editing}
        onClose={() => setEditing(null)}
        onSaved={(a) => {
          setArtworks((arr) => arr.map((x) => (x.id === a.id ? { ...x, ...a } : x)));
          setEditing(null);
        }}
      />
      <CreateArtworkDialog
        open={creating}
        onClose={() => setCreating(false)}
        artists={artists}
        onCreated={(a) => {
          setArtworks((arr) => [a, ...arr]);
          setCreating(false);
        }}
      />
      <CreateAuctionDialog
        artwork={auctionFor}
        onClose={() => setAuctionFor(null)}
        onCreated={(a) => {
          setAuctions((arr) => [a, ...arr]);
          setAuctionFor(null);
          setTab("auctions");
        }}
      />
    </div>
  );
}

/* ---------- Edit Artwork ---------- */
function EditArtworkDialog({ artwork, onClose, onSaved }) {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (artwork) {
      setForm({
        title: artwork.title,
        description: artwork.description,
        price: artwork.price,
        medium: artwork.medium,
        year: artwork.year ?? "",
        dimensions: artwork.dimensions ?? "",
        country: artwork.country ?? "",
        sold: !!artwork.sold,
      });
    }
  }, [artwork]);

  if (!artwork || !form) return null;
  const set = (k) => (e) => setForm({ ...form, [k]: e.target?.value ?? e });

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        ...form,
        price: parseFloat(form.price),
        year: form.year ? parseInt(form.year) : null,
      };
      const { data } = await api.patch(`/admin/artworks/${artwork.id}`, payload);
      toast.success("Artwork updated");
      onSaved(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={!!artwork} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="edit-artwork-dialog" className="rounded-none max-w-2xl border border-[#E5E0D8]">
        <DialogHeader>
          <DialogTitle className="font-serif-display text-3xl text-[#111] tracking-tight">
            Edit · {artwork.title}
          </DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <div className="md:col-span-2">
            <label className="label-eyebrow text-[#555]">Title</label>
            <Input data-testid="edit-title" value={form.title} onChange={set("title")} className={inputCls} />
          </div>
          <div className="md:col-span-2">
            <label className="label-eyebrow text-[#555]">Description</label>
            <Textarea data-testid="edit-desc" rows={3} value={form.description} onChange={set("description")} className="mt-2 rounded-none border border-[#E5E0D8] bg-transparent focus-visible:ring-0 focus-visible:border-[#1E3A8A]" />
          </div>
          <div>
            <label className="label-eyebrow text-[#555]">Price (USD)</label>
            <Input data-testid="edit-price" type="number" step="0.01" min="1" value={form.price} onChange={set("price")} className={inputCls} />
          </div>
          <div>
            <label className="label-eyebrow text-[#555]">Medium</label>
            <Select value={form.medium} onValueChange={(v) => setForm({ ...form, medium: v })}>
              <SelectTrigger data-testid="edit-medium" className={inputCls}><SelectValue /></SelectTrigger>
              <SelectContent className="rounded-none">{MEDIUMS.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <label className="label-eyebrow text-[#555]">Year</label>
            <Input data-testid="edit-year" type="number" value={form.year} onChange={set("year")} className={inputCls} />
          </div>
          <div>
            <label className="label-eyebrow text-[#555]">Dimensions</label>
            <Input data-testid="edit-dimensions" value={form.dimensions} onChange={set("dimensions")} className={inputCls} />
          </div>
          <div>
            <label className="label-eyebrow text-[#555]">Country</label>
            <Input data-testid="edit-country" value={form.country} onChange={set("country")} className={inputCls} />
          </div>
          <div className="flex items-center gap-2 mt-7">
            <input type="checkbox" id="edit-sold" data-testid="edit-sold" checked={form.sold} onChange={(e) => setForm({ ...form, sold: e.target.checked })} />
            <label htmlFor="edit-sold" className="label-eyebrow text-[#555]">Mark as sold</label>
          </div>
        </div>
        <DialogFooter>
          <Button onClick={onClose} variant="outline" className="rounded-none border-[#1E3A8A] text-[#1E3A8A] hover:bg-[#1E3A8A] hover:text-white label-eyebrow">Cancel</Button>
          <Button onClick={save} disabled={saving} data-testid="edit-save-btn" className="rounded-none bg-[#1E3A8A] hover:bg-[#162a66] text-white label-eyebrow">
            {saving ? "Saving…" : "Save changes"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ---------- Create Artwork ---------- */
function CreateArtworkDialog({ open, onClose, artists, onCreated }) {
  const [form, setForm] = useState({
    artist_id: "",
    title: "",
    description: "",
    price: "",
    medium: "Painting",
    image_url: "",
    year: new Date().getFullYear(),
    dimensions: "",
    country: "",
  });
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) {
      setForm({
        artist_id: "",
        title: "",
        description: "",
        price: "",
        medium: "Painting",
        image_url: "",
        year: new Date().getFullYear(),
        dimensions: "",
        country: "",
      });
    }
  }, [open]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target?.value ?? e });

  const handleUpload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", f);
    try {
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((p) => ({ ...p, image_url: data.url }));
      toast.success("Image uploaded");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Upload failed");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const create = async () => {
    if (!form.artist_id || !form.title || !form.description || !form.price || !form.image_url) {
      toast.error("Please fill all required fields and upload an image");
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form, price: parseFloat(form.price), year: parseInt(form.year) || null };
      const { data } = await api.post("/admin/artworks", payload);
      toast.success("Artwork created");
      onCreated(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Create failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="create-artwork-dialog" className="rounded-none max-w-2xl border border-[#E5E0D8] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-serif-display text-3xl text-[#111] tracking-tight">
            Add new artwork
          </DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <div className="md:col-span-2">
            <label className="label-eyebrow text-[#555]">Artist *</label>
            <Select value={form.artist_id} onValueChange={(v) => setForm({ ...form, artist_id: v })}>
              <SelectTrigger data-testid="create-artist" className={inputCls}><SelectValue placeholder="Select artist…" /></SelectTrigger>
              <SelectContent className="rounded-none">
                {artists.map((a) => <SelectItem key={a.id} value={a.id}>{a.name} · {a.country}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="md:col-span-2">
            <label className="label-eyebrow text-[#555]">Image *</label>
            <div className="mt-3 flex flex-col sm:flex-row gap-4 items-start">
              <label htmlFor="create-art-file" className="cursor-pointer rounded-none border border-[#1E3A8A] text-[#1E3A8A] hover:bg-[#1E3A8A] hover:text-white transition-colors px-5 py-3 label-eyebrow">
                {(() => {
                  if (uploading) return "Uploading…";
                  if (form.image_url) return "Replace image";
                  return "Upload from device";
                })()}
                <input id="create-art-file" data-testid="create-art-file" type="file" accept="image/jpeg,image/png,image/webp,image/gif" onChange={handleUpload} disabled={uploading} className="hidden" />
              </label>
              {form.image_url && <img src={resolveImg(form.image_url)} alt="Preview" className="w-20 h-20 object-cover border border-[#E5E0D8]" />}
            </div>
          </div>
          <div className="md:col-span-2">
            <label className="label-eyebrow text-[#555]">Title *</label>
            <Input data-testid="create-title" value={form.title} onChange={set("title")} className={inputCls} />
          </div>
          <div className="md:col-span-2">
            <label className="label-eyebrow text-[#555]">Description *</label>
            <Textarea data-testid="create-desc" rows={3} value={form.description} onChange={set("description")} className="mt-2 rounded-none border border-[#E5E0D8] bg-transparent focus-visible:ring-0 focus-visible:border-[#1E3A8A]" />
          </div>
          <div>
            <label className="label-eyebrow text-[#555]">Price (USD) *</label>
            <Input data-testid="create-price" type="number" step="0.01" min="1" value={form.price} onChange={set("price")} className={inputCls} />
          </div>
          <div>
            <label className="label-eyebrow text-[#555]">Medium *</label>
            <Select value={form.medium} onValueChange={(v) => setForm({ ...form, medium: v })}>
              <SelectTrigger data-testid="create-medium" className={inputCls}><SelectValue /></SelectTrigger>
              <SelectContent className="rounded-none">{MEDIUMS.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <label className="label-eyebrow text-[#555]">Year</label>
            <Input data-testid="create-year" type="number" value={form.year} onChange={set("year")} className={inputCls} />
          </div>
          <div>
            <label className="label-eyebrow text-[#555]">Dimensions</label>
            <Input data-testid="create-dimensions" value={form.dimensions} onChange={set("dimensions")} placeholder="H × W × D in cm  ·  e.g. 140 x 45 x 40 cm" className={inputCls} />
            <p className="text-[10px] text-[#555] mt-1">Sculptures: height first, in cm — drives realistic visualiser placement.</p>
          </div>
          <div className="md:col-span-2">
            <label className="label-eyebrow text-[#555]">Country</label>
            <Input data-testid="create-country" value={form.country} onChange={set("country")} placeholder="e.g. South Africa" className={inputCls} />
          </div>
        </div>
        <DialogFooter>
          <Button onClick={onClose} variant="outline" className="rounded-none border-[#1E3A8A] text-[#1E3A8A] hover:bg-[#1E3A8A] hover:text-white label-eyebrow">Cancel</Button>
          <Button onClick={create} disabled={saving} data-testid="create-save-btn" className="rounded-none bg-[#1E3A8A] hover:bg-[#162a66] text-white label-eyebrow">
            {saving ? "Publishing…" : "Publish to gallery"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ---------- Create Auction ---------- */
function CreateAuctionDialog({ artwork, onClose, onCreated }) {
  const [form, setForm] = useState({ starting_price: "", duration_hours: 48, min_increment: 50 });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (artwork) {
      setForm({
        starting_price: String(Math.round(artwork.price * 0.6)),
        duration_hours: 48,
        min_increment: 50,
      });
    }
  }, [artwork]);

  if (!artwork) return null;
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const create = async () => {
    setSaving(true);
    try {
      const payload = {
        artwork_id: artwork.id,
        starting_price: parseFloat(form.starting_price),
        duration_hours: parseInt(form.duration_hours) || 48,
        min_increment: parseFloat(form.min_increment) || 50,
      };
      const { data } = await api.post("/admin/auctions", payload);
      toast.success("Auction is live");
      onCreated(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not start auction");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={!!artwork} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="create-auction-dialog" className="rounded-none max-w-lg border border-[#E5E0D8]">
        <DialogHeader>
          <DialogTitle className="font-serif-display text-3xl text-[#111] tracking-tight">
            Auction · {artwork.title}
          </DialogTitle>
        </DialogHeader>
        <div className="flex gap-4 items-start">
          <img src={resolveImg(artwork.image_url)} alt={artwork.title} className="w-24 h-24 object-cover border border-[#E5E0D8]" />
          <div>
            <p className="label-eyebrow text-[#1E3A8A]">{artwork.medium} · {artwork.artist_name}</p>
            <p className="text-sm mt-1 text-[#555]">Listed price · ${artwork.price.toLocaleString()}</p>
          </div>
        </div>
        <div className="mt-5 grid grid-cols-1 md:grid-cols-3 gap-5">
          <div>
            <label className="label-eyebrow text-[#555]">Starting price (USD)</label>
            <Input data-testid="auction-start-price" type="number" step="0.01" min="1" value={form.starting_price} onChange={set("starting_price")} className={inputCls} />
          </div>
          <div>
            <label className="label-eyebrow text-[#555]">Duration (hours)</label>
            <Input data-testid="auction-duration" type="number" min="1" max="720" value={form.duration_hours} onChange={set("duration_hours")} className={inputCls} />
          </div>
          <div>
            <label className="label-eyebrow text-[#555]">Min increment</label>
            <Input data-testid="auction-increment" type="number" min="1" value={form.min_increment} onChange={set("min_increment")} className={inputCls} />
          </div>
        </div>
        <DialogFooter>
          <Button onClick={onClose} variant="outline" className="rounded-none border-[#1E3A8A] text-[#1E3A8A] hover:bg-[#1E3A8A] hover:text-white label-eyebrow">Cancel</Button>
          <Button onClick={create} disabled={saving} data-testid="auction-create-btn" className="rounded-none bg-[#1E3A8A] hover:bg-[#162a66] text-white label-eyebrow">
            {saving ? "Starting…" : "Start auction"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
